'use client';
import React from 'react';
import { useForm } from 'react-hook-form';
import type { Language } from '@/lib/i18n';
import { t } from '@/lib/i18n';
import type { Lesson } from '@/lib/mockData';
import { X, Loader2 } from 'lucide-react';

interface LessonFormData {
  titleFr: string;
  titleRu: string;
  descFr: string;
  descRu: string;
  xpReward: number;
  levelNumber: number;
  status: 'locked' | 'available' | 'in_progress' | 'completed';
}

interface Props {
  lesson: Lesson | null;
  language: Language;
  onClose: () => void;
  onSave: () => void;
}

export default function LessonEditorModal({ lesson, language, onClose, onSave }: Props) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LessonFormData>({
    defaultValues: lesson
      ? {
          titleFr: lesson.title.fr,
          titleRu: lesson.title.ru,
          descFr: lesson.description.fr,
          descRu: lesson.description.ru,
          xpReward: lesson.xpReward,
          levelNumber: lesson.levelNumber,
          status: lesson.status,
        }
      : {
          xpReward: 100,
          levelNumber: 1,
          status: 'locked',
        },
  });

  // Backend integration point: POST/PUT /api/lessons
  const onSubmit = async (_data: LessonFormData) => {
    await new Promise((r) => setTimeout(r, 800));
    onSave();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-foreground/40 backdrop-blur-sm animate-fade-in">
      <div className="bg-card rounded-2xl border border-border card-shadow-lg w-full max-w-lg animate-slide-up">
        {/* Modal header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-base font-700 text-foreground">
            {lesson
              ? language === 'fr' ? 'Modifier la leçon' : 'Редактировать урок'
              : language === 'fr' ? 'Nouvelle leçon' : 'Новый урок'}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
          >
            <X size={15} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
          {/* Titles */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-600 text-muted-foreground mb-1.5 uppercase tracking-wide">
                {language === 'fr' ? 'Titre (Français)' : 'Название (Французский)'}
              </label>
              <input
                {...register('titleFr', { required: 'Requis' })}
                className="w-full px-3 py-2.5 bg-input border border-border rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
              {errors.titleFr && <p className="mt-1 text-xs text-danger">{errors.titleFr.message}</p>}
            </div>
            <div>
              <label className="block text-xs font-600 text-muted-foreground mb-1.5 uppercase tracking-wide">
                {language === 'fr' ? 'Titre (Russe)' : 'Название (Русский)'}
              </label>
              <input
                {...register('titleRu', { required: 'Обязательно' })}
                className="w-full px-3 py-2.5 bg-input border border-border rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
              {errors.titleRu && <p className="mt-1 text-xs text-danger">{errors.titleRu.message}</p>}
            </div>
          </div>

          {/* Descriptions */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-600 text-muted-foreground mb-1.5 uppercase tracking-wide">
                {language === 'fr' ? 'Description (FR)' : 'Описание (FR)'}
              </label>
              <textarea
                {...register('descFr')}
                rows={2}
                className="w-full px-3 py-2.5 bg-input border border-border rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
              />
            </div>
            <div>
              <label className="block text-xs font-600 text-muted-foreground mb-1.5 uppercase tracking-wide">
                {language === 'fr' ? 'Description (RU)' : 'Описание (RU)'}
              </label>
              <textarea
                {...register('descRu')}
                rows={2}
                className="w-full px-3 py-2.5 bg-input border border-border rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
              />
            </div>
          </div>

          {/* XP + Level + Status */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-600 text-muted-foreground mb-1.5 uppercase tracking-wide">
                XP
              </label>
              <input
                type="number"
                {...register('xpReward', { min: 1, max: 1000 })}
                className="w-full px-3 py-2.5 bg-input border border-border rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 font-tabular"
              />
            </div>
            <div>
              <label className="block text-xs font-600 text-muted-foreground mb-1.5 uppercase tracking-wide">
                {language === 'fr' ? 'Niveau' : 'Уровень'}
              </label>
              <select
                {...register('levelNumber')}
                className="w-full px-3 py-2.5 bg-input border border-border rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
              >
                {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                  <option key={`level-opt-${n}`} value={n}>{n}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-600 text-muted-foreground mb-1.5 uppercase tracking-wide">
                Statut
              </label>
              <select
                {...register('status')}
                className="w-full px-3 py-2.5 bg-input border border-border rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
              >
                {(['locked', 'available', 'in_progress', 'completed'] as const).map((s) => (
                  <option key={`status-opt-${s}`} value={s}>
                    {t(language, `learning_${s}` as 'learning_locked')}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-2 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 border border-border rounded-xl text-sm font-600 text-muted-foreground hover:bg-secondary hover:text-foreground transition-all active:scale-95"
            >
              {t(language, 'admin_cancel')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex items-center gap-2 px-5 py-2.5 bg-primary text-white rounded-xl text-sm font-600 hover:bg-primary/90 transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isSubmitting && <Loader2 size={14} className="animate-spin" />}
              {t(language, 'admin_save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}